// SPDX-License-Identifier: LicenseRef-Proprietary

import React from 'react';
import type { DashboardSettings } from '../../../store/vaccineDashboardStore';

interface DashboardSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  settings: DashboardSettings;
  onSettingsChange: (settings: Partial<DashboardSettings>) => void;
}

export const DashboardSettingsModal: React.FC<DashboardSettingsModalProps> = ({
  isOpen,
  onClose,
  settings,
  onSettingsChange,
}) => {
  if (!isOpen) return null;

  const handleKPIToggle = (kpiKey: keyof DashboardSettings['visibleKPIs']) => {
    onSettingsChange({
      visibleKPIs: {
        ...settings.visibleKPIs,
        [kpiKey]: !settings.visibleKPIs[kpiKey],
      },
    });
  };

  return (
    <div
      className="dashboard-settings-modal__backdrop"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="dashboard-settings-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dashboard-settings-modal__header">
          <h2 className="dashboard-settings-modal__title">Dashboard Settings</h2>
          <button
            onClick={onClose}
            className="dashboard-settings-modal__close"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <div>
          <div className="dashboard-settings-modal__group">
            <h3 className="dashboard-settings-modal__section-title">
              Visible KPIs
            </h3>
            <p className="dashboard-settings-modal__description">
              Select which Key Performance Indicators to display on the dashboard. The "Big 4" (ICER, QALYs, Budget Impact, BCR) are recommended for primary analysis.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="dashboard-settings-modal__group">
                <div className="dashboard-settings-modal__section-label">
                  Big 4 KPIs (Recommended)
                </div>
                <div className="dashboard-settings-modal__kpi-list">
                  {[
                    { key: 'icer' as const, label: 'ICER (Incremental Cost-Effectiveness Ratio)' },
                    { key: 'qalysGained' as const, label: 'QALYs Gained' },
                    { key: 'budgetImpact' as const, label: 'Budget Impact' },
                    { key: 'bcr' as const, label: 'BCR (Benefit-Cost Ratio)' },
                  ].map(({ key, label }) => (
                    <label
                      key={key}
                      className="dashboard-settings-modal__kpi-item"
                    >
                      <input
                        type="checkbox"
                        className="dashboard-settings-modal__checkbox"
                        checked={settings.visibleKPIs[key]}
                        onChange={() => handleKPIToggle(key)}
                      />
                      <span className="dashboard-settings-modal__kpi-label">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <div className="dashboard-settings-modal__section-label">
                  Additional Details
                </div>
                <div className="dashboard-settings-modal__kpi-list">
                  {[
                    { key: 'lifeYearsGained' as const, label: 'Life-Years Gained' },
                    { key: 'deathsAverted' as const, label: 'Deaths Averted' },
                    { key: 'casesAvoided' as const, label: 'Cases Avoided' },
                    { key: 'hospitalizationsAvoided' as const, label: 'Hospitalizations Avoided' },
                    { key: 'adverseEventCost' as const, label: 'Include Adverse Event Costs' },
                    { key: 'productivityCost' as const, label: 'Productivity Costs/Savings' },
                  ].map(({ key, label }) => (
                    <label
                      key={key}
                      className="dashboard-settings-modal__kpi-item"
                    >
                      <input
                        type="checkbox"
                        className="dashboard-settings-modal__checkbox"
                        checked={settings.visibleKPIs[key]}
                        onChange={() => handleKPIToggle(key)}
                      />
                      <span className="dashboard-settings-modal__kpi-label">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-settings-modal__footer">
          <button
            onClick={onClose}
            className="btn btn-primary"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
